import React, { ReactNode, useMemo, useCallback, useState, useEffect } from 'react'
import type { OvertimeRequest } from '@/types/supabase-types'
import { OvertimeContext } from './OvertimeContext'
import { useAuth } from '@/contexts/auth'
import { UserRole } from '@/types/enums'

import { useOvertimeData } from './hooks/useOvertimeData'
import { useApprovalHandlers } from './hooks/useApprovalHandlers'
import { useSubmissionHandlers } from './hooks/useSubmissionHandlers'

import { useShifts } from './hooks/useShifts'
import { useAnalytics } from './hooks/useAnalytics'
import { useEligibilityCheck } from './hooks/useEligibilityCheck'

import { userProfileToUser } from '@/utils/typeAdapters'
import { toast } from 'sonner'
import { NotificationService } from '@/services/notificationService'
import { submitDepartmentOvertimeRequest } from './utils/requestHandlers'

import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function OvertimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { 
    overtimeRequests, 
    setOvertimeRequests, 
    employees, 
    shifts: defaultShifts, 
    error,
    getOvertimeRequestById,
    getUserRequests,
    getPendingRequests,
    getDepartmentRequests,
    getOvertimeByDepartment,
    getProductivityMetrics,
    getDepartmentComparison,
    getShiftOvertimeTrends,
    getResidencyData,
    getDepartmentEmployees,
    getAllEmployees,
    getAllRequests
  } = useOvertimeData()
  
  // Use our new hooks - cast the shifts to the expected type
  const { shifts: shiftData } = useShifts(defaultShifts as any[])
  const shifts = shiftData as any[] // temporary type fix
  const { getAnalyticsSummary, getEmployeeMonthlyOvertimeHours, getOvertimeTrend } = useAnalytics(overtimeRequests, undefined)
// TODO: Replace 'undefined' with the correct user if available
  const { checkEmployeeOvertimeEligibility } = useEligibilityCheck(overtimeRequests)

  // ✅ NEW: Instant state update functions for new OT requests
  const addOvertimeRequestToState = useCallback((newRequest: OvertimeRequest) => {
    setOvertimeRequests(prev => {
      const updated = [newRequest, ...prev];
      return updated;
    });
    
    // ✅ NEW: Invalidate React Query cache to ensure dashboard gets fresh data
    // This ensures that any components using React Query data will re-render
    queryClient.invalidateQueries({ queryKey: ['overtime_requests'] });
    
  }, [setOvertimeRequests, queryClient]);

  const updateOvertimeRequestInState = useCallback((updatedRequest: OvertimeRequest) => {
    setOvertimeRequests(prev => 
      prev.map(req => req.id === updatedRequest.id ? updatedRequest : req)
    );
    
    // ✅ NEW: Invalidate React Query cache to ensure dashboard gets fresh data
    queryClient.invalidateQueries({ queryKey: ['overtime_requests'] });
  }, [setOvertimeRequests, queryClient]);

  const removeOvertimeRequestFromState = useCallback((requestId: string) => {
    setOvertimeRequests(prev => prev.filter(req => req.id !== requestId));
    
    // ✅ NEW: Invalidate React Query cache to ensure dashboard gets fresh data
    queryClient.invalidateQueries({ queryKey: ['overtime_requests'] });
  }, [setOvertimeRequests, queryClient]);

  // ✅ NEW: Wrapper function for setOvertimeRequests that automatically invalidates cache
  const setOvertimeRequestsWithIncrement = useCallback((updater: React.SetStateAction<OvertimeRequest[]>) => {
    setOvertimeRequests(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      return newState;
    });
    
    // ✅ NEW: Invalidate React Query cache to ensure dashboard gets fresh data
    queryClient.invalidateQueries({ queryKey: ['overtime_requests'] });
  }, [setOvertimeRequests, queryClient]);
  const refreshRequests = useCallback(async (): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Invalidate and immediately refetch
      await queryClient.invalidateQueries({ queryKey: ['overtime_requests'] });
      await queryClient.refetchQueries({ queryKey: ['overtime_requests'] });
      
      return true;
    } catch {
      toast.error('Failed to refresh overtime requests');
      return false;
    } finally {
      setLoading(false);
    }
  }, [queryClient]);
  
  // Get the current authenticated user from the auth context
  const { user: authUser } = useAuth();
  
  // Prefetch data on login: justification reasons, company holidays, recent unique employees by OT
  useEffect(() => {
    // let cancelled = false;
    const prefetchOnLogin = async () => {
      try {
        if (!authUser?.companyId) return;
        const companyId = authUser.companyId;
        
        // Prefetch justification reasons
        queryClient.prefetchQuery({
          queryKey: ['overtime_reasons', companyId],
          queryFn: async () => {
            const { data, error } = await supabase
              .from('overtime_reasons')
              .select('*')
              .eq('company_id', companyId)
              .eq('is_active', true)
              .order('main_title', { ascending: true })
              .order('reason', { ascending: true });
            if (error) throw error;
            return data || [];
          },
          staleTime: 10 * 60 * 1000,
        });
        
        // Prefetch holidays for this company
        queryClient.prefetchQuery({
          queryKey: ['companyHolidays', companyId],
          queryFn: async () => {
            const { data, error } = await supabase
              .from('holidays')
              .select('*')
              .eq('company_id', companyId)
              .eq('is_public', true);
            if (error) throw error;
            return data || [];
          },
          staleTime: 60 * 60 * 1000,
        });
        
        // Prefetch recent unique employees based on latest overtime requests
        queryClient.prefetchQuery({
          queryKey: ['recentOTEmployees', companyId],
          queryFn: async () => {
            const { data, error } = await supabase
              .from('overtime_requests')
              .select('employee_id, employee_name, submitted_at, employees!overtime_requests_employee_id_fkey(company_id)')
              .eq('employees.company_id', companyId)
              .order('submitted_at', { ascending: false })
              .limit(100);
            if (error) throw error;
            const uniqueMap = new Map<string, { employee_id: string; employee_name: string | null }>();
            (data || []).forEach((row: { employee_id?: string; employee_name?: string | null }) => {
              const id = row.employee_id as string;
              if (id && !uniqueMap.has(id)) {
                uniqueMap.set(id, { employee_id: id, employee_name: row.employee_name || null });
              }
            });
            return Array.from(uniqueMap.values());
          },
          staleTime: 30 * 60 * 1000,
        });
        
        // ✅ NEW: Prefetch essential dashboard data for immediate loading
        // Prefetch employees data for dashboard
        queryClient.prefetchQuery({
          queryKey: ['employees', undefined, 0, 1000, 'name', 'asc'],
          queryFn: async () => {
            const { data, error } = await supabase
              .from('employees')
              .select('*', { count: 'exact' })
              .eq('company_id', companyId)
              .order('first_name', { ascending: true })
              .order('last_name', { ascending: true })
              .range(0, 999);
            if (error) throw error;
            
            const employees = (data || []).map(emp => ({
              id: emp.id,
              email: emp.email || '',
              role: emp.role || UserRole.EMPLOYEE,
              firstName: emp.first_name || '',
              lastName: emp.last_name || '',
              name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
              department: emp.department || '',
              position: emp.position || '',
              companyId: emp.company_id,
              status: emp.status || 'active',
              shift: emp.shift || '',
              residency: emp.residency || 'resident',
              workerType: emp.worker_type || 'full_time',
              employee_id: emp.employee_id || ''
            }));
            
            return {
              employees,
              totalCount: employees.length,
              page: 0,
              pageSize: 1000,
              totalPages: 1
            };
          },
          staleTime: 5 * 60 * 1000, // 5 minutes
        });
        
        // Prefetch overtime requests data for dashboard
        queryClient.prefetchQuery({
          queryKey: ['overtimeRequests', undefined, undefined, undefined, companyId, undefined, undefined, 0, 10000],
          queryFn: async () => {
            // Use pagination to fetch all data, not just 10000 rows
            const baseQuery = supabase
              .from('overtime_requests')
              .select(`
                *, 
                employees!overtime_requests_employee_id_fkey(first_name, last_name, department, company_id),
                overtime_reasons!justification(main_title, reason)
              `, { count: 'exact' })
              .eq('employees.company_id', companyId)
              .order('date', { ascending: false });
            
            // Fetch all data in chunks to bypass row limit
            const allData: any[] = [];
            let offset = 0;
            const chunkSize = 1000;
            
            while (true) {
              const { data: chunkData, error: chunkError } = await baseQuery.range(offset, offset + chunkSize - 1);
              
              if (chunkError) throw chunkError;
              
              if (!chunkData || chunkData.length === 0) {
                break; // No more data to fetch
              }
              
              allData.push(...chunkData);
              
              if (chunkData.length < chunkSize) {
                break; // Last chunk, we've got all data
              }
              
              offset += chunkSize;
            }
            
            const data = allData;
            
            const transformedRequests = (data || []).map(request => ({
              ...request,
              justification_text: (() => {
                if (request.overtime_reasons) {
                  if (Array.isArray(request.overtime_reasons) && request.overtime_reasons.length > 0) {
                    return request.overtime_reasons[0].reason || request.overtime_reasons[0].main_title || 'Unknown reason';
                  } else if (typeof request.overtime_reasons === 'object' && request.overtime_reasons !== null) {
                    return request.overtime_reasons.reason || request.overtime_reasons.main_title || 'Unknown reason';
                  }
                }
                return 'Unknown reason';
              })()
            }));
            
            return {
              requests: transformedRequests,
              totalCount: transformedRequests.length,
              page: 0,
              pageSize: 10000,
              totalPages: 1
            };
          },
          staleTime: 30 * 1000, // 30 seconds - shorter for real-time data
        });
      } catch {
        // ignore prefetch errors
      }
    };
    prefetchOnLogin();
    return () => { /* cancelled = true; */ };
  }, [authUser?.companyId, queryClient]);
  
  // Prepare user for submission and approval handlers
  const adaptedUser = userProfileToUser(authUser)
  
  // Import submission handlers and assign to local variable
  const {
    submitOvertimeRequest,
    // TODO: If prepareOvertimeRequest and submitPendingRequest are provided by this hook, destructure them here.
  } = useSubmissionHandlers(adaptedUser, employees, overtimeRequests, setOvertimeRequests);
  
  // Import approval handlers
  const {
    approveOvertimeRequest: hookApproveOvertimeRequest,
    rejectOvertimeRequest: hookRejectOvertimeRequest,
    approveOvertimeRequestAsVP: hookApproveOvertimeRequestAsVP
  } = useApprovalHandlers(
    adaptedUser, 
    setOvertimeRequests,
    refreshRequests // Pass the refresh function
  )
  
  // Use the destructured handler directly

  /**
   * Delete a notification
   */
  // Removed unused local deleteNotification function as it is provided via context.

  // Create the context value with all our functions and values
  const contextValue = useMemo(() => {
    const isPendingApproval = false;

    return {
      // ✅ NEW: Use the wrapper function that automatically increments dashboard count
      setOvertimeRequests: setOvertimeRequestsWithIncrement,
      // Notification handlers
      deleteNotification: async (id: string): Promise<void> => {
        try {
          await NotificationService.deleteNotification(id);
          toast.success('Notification deleted successfully');
          refreshRequests();
        } catch {
          toast.error('Failed to delete notification');
        }
      },
      markNotificationAsRead: async (id: string): Promise<void> => {
        try {
          await NotificationService.markAsRead(id);
          toast.success('Notification marked as read');
          refreshRequests();
        } catch {
          toast.error('Failed to mark notification as read');
        }
      },
      // Overtime request handlers
      submitOvertimeRequest,
      // Add submitDepartmentOvertimeRequest, prepareOvertimeRequest, submitPendingRequest to context
      submitDepartmentOvertimeRequest: (
        employeeId: string,
        requests: Array<Omit<OvertimeRequest, 'id' | 'status' | 'submittedAt' | 'submittedById' | 'submittedByName'>>
      ) => submitDepartmentOvertimeRequest(adaptedUser, employees, overtimeRequests, setOvertimeRequests, requests),
      // TODO: Replace any with correct types. If prepareOvertimeRequest and submitPendingRequest are available, wire them similarly:
      prepareOvertimeRequest: async () => { return false; },
      submitPendingRequest: async () => { return false; },
      approveOvertimeRequest: hookApproveOvertimeRequest,
      rejectOvertimeRequest: hookRejectOvertimeRequest,
      approveOvertimeRequestAsVP: hookApproveOvertimeRequestAsVP,
      refreshRequests,
                        // ✅ NEW: Instant state update functions
                  addOvertimeRequestToState,
                  updateOvertimeRequestInState,
                  removeOvertimeRequestFromState,
                  // ✅ REMOVED: No longer needed since dashboard uses reactive stats
                  incrementTotalRequests: () => {
                    // Dashboard stats are now calculated directly from overtimeRequests array
                  },
      // Data and state
      overtimeRequests,
      employees,
      loading,
      error,
      user: null,
      shifts,
      getOvertimeRequestById,
      getUserRequests,
      getPendingRequests,
      getDepartmentRequests,
      getOvertimeByDepartment,
      getOvertimeTrend,
      getProductivityMetrics,
      getDepartmentComparison,
      getShiftOvertimeTrends,
      getResidencyData,
      getAnalyticsSummary,
      getEmployeeMonthlyOvertimeHours,
      getDepartmentEmployees,
      getAllEmployees,
      getAllRequests,
      getShifts: () => shifts as any[],
      checkEmployeeOvertimeEligibility,
      isPendingApproval,
      isLoading: loading,
    };
  }, [
    overtimeRequests,
    employees,
    loading,
    error,
    
    shifts,
    submitOvertimeRequest,
    hookApproveOvertimeRequest,
    hookRejectOvertimeRequest,
    refreshRequests,
    hookApproveOvertimeRequestAsVP,
    getOvertimeRequestById,
    getUserRequests,
    getPendingRequests,
    getDepartmentRequests,
    getOvertimeByDepartment,
    getOvertimeTrend,
    getProductivityMetrics,
    getDepartmentComparison,
    getShiftOvertimeTrends,
    getResidencyData,
    getDepartmentEmployees,
    getAllEmployees,
    getAllRequests,
    
    checkEmployeeOvertimeEligibility,
    getEmployeeMonthlyOvertimeHours,
    getAnalyticsSummary,

    adaptedUser,
    setOvertimeRequests,
    // ✅ NEW: Add instant state update function dependencies
    addOvertimeRequestToState,
    updateOvertimeRequestInState,
    removeOvertimeRequestFromState,
    // ✅ NEW: Add the wrapper function dependency
    setOvertimeRequestsWithIncrement
  ]);

  return (
    <OvertimeContext.Provider value={contextValue}>
      {children}
    </OvertimeContext.Provider>
  );
}